/**
 * Shared validation utilities
 */

// Password validation constants - must match backend
export const PASSWORD_MIN_LENGTH = 12;

/**
 * i18n key for the password hint rendered under a password field.
 * Consume it through `usePasswordHint()` (`shared/hooks`) rather than translating
 * it at each call site — the key and its `min` param belong together.
 */
export const PASSWORD_HINT_KEY = 'common:validation.minLength';

/**
 * Validate password meets requirements.
 *
 * Returns an i18n key (never a rendered sentence) like every other validator in
 * this file: a plain `.ts` util has no `t()`, and an English literal returned
 * from here reaches the DOM untranslated on six auth surfaces.
 *
 * @param password Password to validate
 * @returns Object with isValid boolean, plus an i18n key + params when invalid
 */
export function validatePassword(password: string): {
  isValid: boolean;
  errorKey?: string;
  errorParams?: { min: number };
} {
  if (!password) {
    return { isValid: false, errorKey: 'common:validation.passwordRequired' };
  }
  if (password.length < PASSWORD_MIN_LENGTH) {
    return {
      isValid: false,
      errorKey: 'common:validation.passwordMinLength',
      errorParams: { min: PASSWORD_MIN_LENGTH },
    };
  }
  return { isValid: true };
}

/**
 * Check if a string is a valid UUID format
 * @param value String to validate
 * @returns true if value is a valid UUID
 */
export function isValidUUID(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

/**
 * Characters that are never allowed in an email address.
 * Used for real-time input filtering.
 */
const INVALID_EMAIL_CHARS = /[<>()[\]\\,;:\s"]/g;

/**
 * Standard email validation pattern.
 * Validates format: local-part@domain.tld
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Filter out characters that are never allowed in email addresses.
 * Call this on input change to prevent invalid characters from being entered.
 * @param value The input value to filter
 * @returns The filtered value with invalid characters removed
 */
export function filterEmailInput(value: string): string {
  return value.replace(INVALID_EMAIL_CHARS, '');
}

/**
 * Validate email format against standard patterns.
 * Call this on blur to check if the email is valid.
 * @param email Email to validate
 * @returns Object with isValid boolean and optional error key for i18n
 */
export function validateEmail(email: string): { isValid: boolean; errorKey?: string } {
  // Empty email is valid (field is optional)
  if (!email || email.trim() === '') {
    return { isValid: true };
  }

  // Check format against standard pattern
  if (!EMAIL_PATTERN.test(email)) {
    return { isValid: false, errorKey: 'common:validation.email' };
  }

  return { isValid: true };
}

// ============================================================================
// URL Validation
// ============================================================================

/**
 * Characters that should be filtered from URL input (control chars, some special chars).
 * More permissive than email since URLs can contain many special characters.
 */
const INVALID_URL_CHARS = /[\s<>"{}|\\^`]/g;

/**
 * URL validation pattern - checks for valid URL format.
 * Accepts http://, https://, or protocol-relative URLs.
 */
const URL_PATTERN = /^(https?:\/\/)?[\w-]+(\.[\w-]+)+([\w.,@?^=%&:/~+#-]*[\w@?^=%&/~+#-])?$/i;

/**
 * Filter out characters that are never allowed in URLs.
 * @param value The input value to filter
 * @returns The filtered value with invalid characters removed
 */
export function filterUrlInput(value: string): string {
  return value.replace(INVALID_URL_CHARS, '');
}

/**
 * True when the value is a complete URL or bare domain — `https://acme.com/about`,
 * `www.acme.com`, `acme.com`. False for anything that is merely text, including a
 * company name ("Acme Industries") or an email address.
 *
 * Unlike {@link validateUrl} an empty string is NOT url-like: callers use this to
 * decide whether a value is actionable, not whether an optional field is valid.
 */
export function isUrlLike(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }
  return URL_PATTERN.test(trimmed);
}

/**
 * Pattern for a LinkedIn member profile URL. Two things it deliberately rejects:
 * - `linkedin.com/in/` with no vanity slug — that identifies nobody.
 * - Anything where the URL is merely *embedded*, because the pattern is anchored
 *   at both ends. An email-signature line ("LinkedIn: https://…/in/jane") or a
 *   tracking redirect (`…/r?dest=https://linkedin.com/in/jane`) is not a profile
 *   URL, and treating it as one would hand the whole blob to the AI.
 * Any `linkedin.com` subdomain is allowed — country hosts like `il.linkedin.com`
 * are real profile URLs.
 */
const LINKEDIN_PROFILE_PATTERN = /^(https?:\/\/)?([\w-]+\.)*linkedin\.com\/in\/[^/\s?#]+[^\s]*$/i;

/**
 * True when the value IS a LinkedIn member profile URL (not merely contains one).
 * @param value Value to test (with or without protocol / `www.`)
 */
export function isLinkedInProfileUrl(value: string): boolean {
  return LINKEDIN_PROFILE_PATTERN.test(value.trim());
}

/**
 * Validate URL format.
 * @param url URL to validate
 * @returns Object with isValid boolean and optional error key for i18n
 */
export function validateUrl(url: string): { isValid: boolean; errorKey?: string } {
  // Empty URL is valid (field is optional)
  if (!url || url.trim() === '') {
    return { isValid: true };
  }

  // Check format against URL pattern
  if (!URL_PATTERN.test(url)) {
    return { isValid: false, errorKey: 'common:validation.url' };
  }

  return { isValid: true };
}

// ============================================================================
// Domain Validation
// ============================================================================

/**
 * Characters allowed in domain names (alphanumeric, hyphen, dot).
 */
const INVALID_DOMAIN_CHARS = /[^a-zA-Z0-9.-]/g;

/**
 * Domain validation pattern - validates domain format without protocol.
 * Examples: example.com, sub.example.co.uk
 */
const DOMAIN_PATTERN = /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

/**
 * Filter input to only allow domain-valid characters.
 * @param value The input value to filter
 * @returns The filtered value with invalid characters removed
 */
export function filterDomainInput(value: string): string {
  return value.toLowerCase().replace(INVALID_DOMAIN_CHARS, '');
}

/**
 * Validate domain format (without protocol).
 * @param domain Domain to validate
 * @returns Object with isValid boolean and optional error key for i18n
 */
export function validateDomain(domain: string): { isValid: boolean; errorKey?: string } {
  // Empty domain is valid (field is optional)
  if (!domain || domain.trim() === '') {
    return { isValid: true };
  }

  // Check format against domain pattern
  if (!DOMAIN_PATTERN.test(domain)) {
    return { isValid: false, errorKey: 'common:validation.domain' };
  }

  return { isValid: true };
}

// ============================================================================
// Phone Number Validation
// ============================================================================

/**
 * Characters allowed in phone numbers:
 * - Digits (0-9)
 * - Plus sign (+) at the beginning for international format
 * - Spaces, hyphens, and parentheses for formatting
 * - Letter 'x' or 'X' for extension
 */
const INVALID_PHONE_CHARS = /[^0-9+\-\s()xX]/g;

/**
 * Phone validation pattern.
 * Allows:
 * - Optional + at the start for international format
 * - Digits, spaces, hyphens, parentheses for formatting
 * - Optional extension with 'x' or 'ext' followed by digits
 * Minimum 7 digits required (shortest valid phone numbers)
 */
const PHONE_PATTERN = /^(\+)?[\d\s\-()]+(\s*(x|ext\.?)\s*\d+)?$/i;

/**
 * Filter input to only allow phone number characters.
 * @param value The input value to filter
 * @returns The filtered value with invalid characters removed
 */
export function filterPhoneInput(value: string): string {
  // Allow + only at the beginning
  const filtered = value.replace(INVALID_PHONE_CHARS, '');
  // Remove any + that isn't at the start
  const plusIndex = filtered.indexOf('+');
  if (plusIndex > 0) {
    return filtered.slice(0, plusIndex) + filtered.slice(plusIndex).replace(/\+/g, '');
  }
  return filtered;
}

/**
 * Validate phone number format.
 * @param phone Phone number to validate
 * @returns Object with isValid boolean and optional error key for i18n
 */
export function validatePhone(phone: string): { isValid: boolean; errorKey?: string } {
  // Empty phone is valid (field is optional)
  if (!phone || phone.trim() === '') {
    return { isValid: true };
  }

  // Check format against phone pattern
  if (!PHONE_PATTERN.test(phone)) {
    return { isValid: false, errorKey: 'common:validation.phone' };
  }

  // Check minimum digit count (at least 7 digits for valid phone numbers)
  const digitCount = (phone.match(/\d/g) || []).length;
  if (digitCount < 7) {
    return { isValid: false, errorKey: 'common:validation.phoneTooShort' };
  }

  // Check maximum reasonable length (15 digits is E.164 max + some for extension)
  if (digitCount > 20) {
    return { isValid: false, errorKey: 'common:validation.phoneTooLong' };
  }

  return { isValid: true };
}

// ============================================================================
// HS Code Validation (Harmonized System code for international trade)
// ============================================================================

/**
 * Characters allowed in HS codes (digits and dots).
 */
const INVALID_HS_CODE_CHARS = /[^0-9.]/g;

/**
 * HS Code validation pattern.
 * Standard formats:
 * - 4 digits (chapter.heading): 8471
 * - 6 digits (subheading): 847130 or 8471.30
 * - 8-10 digits (national tariff lines): 84713000 or 8471.30.00
 */
const HS_CODE_PATTERN = /^(\d{4}|\d{4}\.\d{2}|\d{6}|\d{4}\.\d{2}\.\d{2}|\d{8}|\d{4}\.\d{2}\.\d{2}\.\d{2}|\d{10})$/;

/**
 * Filter input to only allow HS code characters (digits and dots).
 * @param value The input value to filter
 * @returns The filtered value with invalid characters removed
 */
export function filterHsCodeInput(value: string): string {
  return value.replace(INVALID_HS_CODE_CHARS, '');
}

/**
 * Validate HS code format.
 * @param hsCode HS code to validate
 * @returns Object with isValid boolean and optional error key for i18n
 */
export function validateHsCode(hsCode: string): { isValid: boolean; errorKey?: string } {
  // Empty HS code is valid (field is optional)
  if (!hsCode || hsCode.trim() === '') {
    return { isValid: true };
  }

  // Check format against HS code pattern
  if (!HS_CODE_PATTERN.test(hsCode)) {
    return { isValid: false, errorKey: 'common:validation.hsCode' };
  }

  return { isValid: true };
}
